import type { ReactNode } from "react"

import {
  CircleAlertIcon,
  CircleCheckIcon,
  LoaderCircleIcon,
} from "lucide-react"

import type { SigningPageState } from "@/components/applications/sign/signingPageState"
import { ButtonBack } from "@/components/buttons/ButtonBack"
import { ButtonNext } from "@/components/buttons/ButtonNext"
import { EversignContainer } from "@/components/eversign/EversignContainer"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"

type SigningPageProps = {
  state: SigningPageState
  supportEmail: string
  onBack: () => void
  onSend: () => void
  onReconcile: () => void
  onContinue: () => void
}

export function SigningPage({
  state,
  supportEmail,
  onBack,
  onSend,
  onReconcile,
  onContinue,
}: SigningPageProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Sign Credit Application</CardTitle>
      </CardHeader>
      <SigningPageContent
        state={state}
        supportEmail={supportEmail}
        onReconcile={onReconcile}
      />
      <SigningPageFooter
        state={state}
        onBack={onBack}
        onSend={onSend}
        onContinue={onContinue}
      />
    </Card>
  )
}

type StatusPanelProps = {
  icon?: ReactNode
  title: string
  children: ReactNode
}

/**
 * A centered signing status: a short verdict and one supporting paragraph.
 * Icons appear only when they carry meaning (success, warning, in progress);
 * neutral states lean on the copy alone.
 */
function StatusPanel({ icon, title, children }: StatusPanelProps) {
  return (
    <div className="mx-auto max-w-md space-y-3 py-10 text-center">
      <h2 className="flex items-center justify-center gap-3 text-2xl font-medium text-balance">
        {icon}
        {title}
      </h2>
      <p className="text-muted-foreground text-sm leading-relaxed text-balance">
        {children}
      </p>
    </div>
  )
}

function SupportLink({ email }: { email: string }) {
  return (
    <a href={`mailto:${email}`} className="font-medium underline">
      {email}
    </a>
  )
}

type SigningPageContentProps = Pick<
  SigningPageProps,
  "state" | "supportEmail" | "onReconcile"
>

function SigningPageContent({
  state,
  supportEmail,
  onReconcile,
}: SigningPageContentProps) {
  return (
    <CardContent className="space-y-5">
      {state.status === "ready-to-send" ||
      state.status === "sending-unavailable" ? (
        <StatusPanel title="Ready to request signatures">
          Each signer will get a secure signing link by email. Review the
          application details, then send the request when you are ready.
        </StatusPanel>
      ) : null}

      {state.status === "sending-unavailable" ? (
        <Alert variant="warning">
          <CircleAlertIcon />
          <AlertTitle>Sending unavailable</AlertTitle>
          <AlertDescription>
            <p>
              Application editing permissions are required to send signing
              requests.
            </p>
          </AlertDescription>
        </Alert>
      ) : null}

      {state.status === "sending" ? (
        <StatusPanel
          icon={
            <LoaderCircleIcon className="text-primary size-8 animate-spin" />
          }
          title="Sending signing requests"
        >
          We are preparing the application and emailing each signer a secure
          link. This only takes a moment.
        </StatusPanel>
      ) : null}

      {state.status === "pending" ? (
        <PendingContent
          state={state}
          supportEmail={supportEmail}
          onReconcile={onReconcile}
        />
      ) : null}

      {state.status === "signed" ? (
        <StatusPanel
          icon={<CircleCheckIcon className="text-success/80 size-8" />}
          title="Application signed"
        >
          The Boundless application has been signed.
        </StatusPanel>
      ) : null}

      {state.status === "declined" ? (
        <StatusPanel
          icon={<CircleAlertIcon className="text-destructive/80 size-8" />}
          title="Signing request declined"
        >
          An owner declined to sign the application. Contact Boundless support
          at <SupportLink email={supportEmail} /> to work out next steps.
        </StatusPanel>
      ) : null}
    </CardContent>
  )
}

type PendingContentProps = {
  state: Extract<SigningPageState, { status: "pending" }>
  supportEmail: string
  onReconcile: () => void
}

function PendingContent({
  state,
  supportEmail,
  onReconcile,
}: PendingContentProps) {
  if (state.signerEvidenceInvalid) {
    return (
      <StatusPanel
        icon={<CircleAlertIcon className="text-warning-foreground size-8" />}
        title="Signature request needs support"
      >
        We could not verify the signers recorded for this request. Contact
        Boundless support at <SupportLink email={supportEmail} /> for help.
      </StatusPanel>
    )
  }

  if (state.providerError) {
    return (
      <StatusPanel
        icon={<CircleAlertIcon className="text-warning-foreground size-8" />}
        title="Signature status is unavailable"
      >
        We could not reach the signing service to check for updates. Refresh the
        page to try again, or contact Boundless support at{" "}
        <SupportLink email={supportEmail} />.
      </StatusPanel>
    )
  }

  if (state.signingUrl) {
    return (
      <>
        <div className="space-y-0.5">
          <p className="font-semibold">Your signature is needed</p>
          <p className="text-muted-foreground text-sm leading-relaxed">
            Sign below, or use the secure link sent to your email.
          </p>
        </div>
        <EversignContainer
          className="mx-auto min-h-[62vh] w-full min-w-0 sm:min-h-[70vh] lg:min-h-[640px] [&>iframe]:!w-full [&>iframe]:!max-w-full"
          embeddedSigningUrl={state.signingUrl}
          onSigned={onReconcile}
          onError={onReconcile}
        />
      </>
    )
  }

  if (state.stale && state.signerSigned) {
    return (
      <StatusPanel
        icon={<CircleAlertIcon className="text-warning-foreground size-8" />}
        title="Company ownership has changed"
      >
        Your signature was recorded, but ownership details changed after
        signatures were requested. Contact Boundless support at{" "}
        <SupportLink email={supportEmail} /> for help.
      </StatusPanel>
    )
  }

  if (state.stale) {
    return (
      <StatusPanel
        icon={<CircleAlertIcon className="text-warning-foreground size-8" />}
        title="Company ownership has changed"
      >
        Ownership details changed after signatures were requested. To make sure
        the right people sign, contact Boundless support at{" "}
        <SupportLink email={supportEmail} />.
      </StatusPanel>
    )
  }

  if (state.signerSigned) {
    return (
      <StatusPanel
        icon={<CircleCheckIcon className="text-success/80 size-8" />}
        title="Document signed successfully!"
      >
        You&apos;ve completed your part of the Boundless application. Once all
        required signatures are complete, you&apos;ll be able to submit your
        application to lenders.
      </StatusPanel>
    )
  }

  if (state.signerSigned === false) {
    return (
      <StatusPanel title="Sign from your email">
        Use the secure signing link we sent to your email. If you cannot find
        it, contact Boundless support at <SupportLink email={supportEmail} />.
      </StatusPanel>
    )
  }

  return (
    <StatusPanel title="Waiting for signatures">
      Nothing is needed from you here. The signers have received secure links by
      email.
    </StatusPanel>
  )
}

type SigningPageFooterProps = Pick<
  SigningPageProps,
  "state" | "onBack" | "onSend" | "onContinue"
>

function SigningPageFooter({
  state,
  onBack,
  onSend,
  onContinue,
}: SigningPageFooterProps) {
  return (
    <CardFooter className="w-full justify-end">
      <ButtonBack onClick={onBack} />
      {state.status === "ready-to-send" ? (
        <ButtonNext type="button" onClick={onSend}>
          Send for signatures
        </ButtonNext>
      ) : null}
      {state.status === "sending" ? (
        <ButtonNext type="button" disabled isLoading>
          Sending
        </ButtonNext>
      ) : null}
      {state.status === "pending" || state.status === "signed" ? (
        <ButtonNext type="button" onClick={onContinue}>
          Continue
        </ButtonNext>
      ) : null}
    </CardFooter>
  )
}
